import { EditorChevronDown } from '@blocksuite/affine-components/toolbar';
import {
  getBlocksuiteLocaleKey,
  LocaleProvider,
  type ToolbarContext,
  translateBlocksuiteTextWithTextFallback,
} from '@blocksuite/affine-shared/services';
import type { BlockComponent } from '@blocksuite/std';
import { html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';

import type { Menu, MenuItem } from './types';

function translateMenuText(
  context: ToolbarContext | undefined,
  kind: 'label' | 'tooltip' | 'item',
  fallback: string
) {
  return translateBlocksuiteTextWithTextFallback(
    context?.std.getOptional(LocaleProvider),
    getBlocksuiteLocaleKey(`toolbar.menu.${kind}`, fallback),
    fallback,
    `toolbar.menu.${kind}`
  );
}

export function renderCurrentMenuItemWith<T, F extends keyof MenuItem<T>>(
  items: MenuItem<T>[],
  currentValue: T,
  field: F
) {
  return items.find(({ value }) => value === currentValue)?.[field];
}

export function renderMenu<T>({
  label,
  tooltip,
  icon,
  items,
  currentValue,
  onPick,
  context,
}: Menu<T>) {
  const translatedLabel = translateMenuText(context, 'label', label);
  const translatedTooltip = translateMenuText(
    context,
    'tooltip',
    tooltip ?? label
  );

  return html`
    <editor-menu-button
      aria-label="${`${translatedLabel.toLowerCase()}-menu`}"
      .button=${html`
        <editor-icon-button
          aria-label="${translatedLabel}"
          .tooltip="${translatedTooltip}"
        >
          ${icon ?? renderCurrentMenuItemWith(items, currentValue, 'icon')}
          ${EditorChevronDown}
        </editor-icon-button>
      `}
    >
      ${renderMenuItems(items, currentValue, onPick, context)}
    </editor-menu-button>
  `;
}

export function renderMenuItems<T>(
  items: MenuItem<T>[],
  currentValue: T,
  onPick: (value: T) => void,
  context?: ToolbarContext
) {
  return repeat(
    items,
    item => item.value,
    ({ key, value, icon, disabled }) => {
      const translatedKey = key
        ? translateMenuText(context, 'item', key)
        : undefined;

      return html`
        <editor-icon-button
          aria-label="${ifDefined(translatedKey)}"
          .disabled=${ifDefined(disabled)}
          .tooltip="${ifDefined(translatedKey)}"
          .active="${currentValue === value}"
          .activeMode="${'background'}"
          @click=${() => onPick(value)}
        >
          ${icon}
        </editor-icon-button>
      `;
    }
  );
}

export function getRootBlock(ctx: ToolbarContext): BlockComponent | null {
  const rootModel = ctx.store.root;
  if (!rootModel) return null;

  return ctx.view.getBlock(rootModel.id);
}
